import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ImagePlus, X } from "lucide-react";

import Button from "../../../../../components/buttons/Button";
import TextField from "../../../../../components/input/textfield/TextField";
import TextArea from "../../../../../components/input/textarea/TextArea";
import Select from "../../../../../components/input/select/Select";

import { useCreatorStreams } from "../../../creators/hooks/useCreatorStreams";
import * as service from "../../../creators/services/creatorStreamsService";

import { compressImageFile } from "../../../../../utils/helpers/compression/imageCompressor";
import { uploadThumbnailToS3 } from "../../../../../utils/helpers/s3Uploader/uploadThumbnailToS3";

import "./CreatorCreateStreamForm.css";

const STREAM_TYPE_OPTIONS = [
  { label: "Match", value: "match" },
  { label: "Training", value: "training" },
  { label: "Event", value: "event" },
];

const ACCESS_TYPE_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Group", value: "group" },
  { label: "Private", value: "private" },
];

const createInitialFormData = () => ({
  title: "",
  description: "",
  streamType: "match",
  accessType: "public",
  scheduledStart: "",
  recordingEnabled: false,
  thumbnailFile: null,
  thumbnailPreview: "",
});

const getRequestError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const getAccessNote = (accessType) => {
  switch (accessType) {
    case "unlisted":
      return "Only people with access to this stream can view it.";

    case "group":
      return "After creating the stream, select permitted groups in Broadcast Setup.";

    case "private":
      return "Only you can access this stream.";

    default:
      return "This stream will be publicly available.";
  }
};

export default function CreatorCreateStreamForm({
  username,
  onClose,
  onSuccess,
}) {
  const { loading, error, createStream } = useCreatorStreams(username);

  const [formData, setFormData] = useState(createInitialFormData);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailProgress, setThumbnailProgress] = useState(0);

  const thumbnailInputRef = useRef(null);
  const thumbnailPreviewRef = useRef("");
  const submittingRef = useRef(false);
  const mountedRef = useRef(true);

  const isBusy = loading || submitting || thumbnailUploading;

  const title = formData.title.trim();
  const description = formData.description.trim();

  const validStreamType = STREAM_TYPE_OPTIONS.some(
    (option) => option.value === formData.streamType,
  );

  const validAccessType = ACCESS_TYPE_OPTIONS.some(
    (option) => option.value === formData.accessType,
  );

  const canSubmit =
    Boolean(username) &&
    Boolean(title) &&
    validStreamType &&
    validAccessType &&
    !isBusy;

  const errorMessage =
    formError ||
    (typeof error === "string"
      ? error
      : error
        ? getRequestError(error, "")
        : "");

  const progress = Math.min(
    100,
    Math.max(0, Math.round(Number(thumbnailProgress) || 0)),
  );

  const submitLabel = thumbnailUploading
    ? `Uploading ${progress}%`
    : isBusy
      ? "Creating..."
      : "Create Stream";

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      if (thumbnailPreviewRef.current) {
        URL.revokeObjectURL(thumbnailPreviewRef.current);
        thumbnailPreviewRef.current = "";
      }
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !submittingRef.current && !isBusy) {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isBusy, onClose]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));

    setFormError("");
  };

  const handleThumbnailChange = (event) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file || isBusy) return;

    setFormError("");

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setFormError("Please select a JPG, PNG or WebP image.");
      return;
    }

    const previousPreview = thumbnailPreviewRef.current;
    const preview = URL.createObjectURL(file);

    thumbnailPreviewRef.current = preview;

    setFormData((previous) => ({
      ...previous,
      thumbnailFile: file,
      thumbnailPreview: preview,
    }));

    if (previousPreview) {
      URL.revokeObjectURL(previousPreview);
    }

    setThumbnailProgress(0);
  };

  const handleRemoveThumbnail = () => {
    if (isBusy || submittingRef.current) return;

    if (thumbnailPreviewRef.current) {
      URL.revokeObjectURL(thumbnailPreviewRef.current);
      thumbnailPreviewRef.current = "";
    }

    setFormData((previous) => ({
      ...previous,
      thumbnailFile: null,
      thumbnailPreview: "",
    }));

    setThumbnailProgress(0);

    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    if (thumbnailPreviewRef.current) {
      URL.revokeObjectURL(thumbnailPreviewRef.current);
      thumbnailPreviewRef.current = "";
    }

    setFormData(createInitialFormData());
    setFormError("");
    setThumbnailProgress(0);

    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    if (isBusy || submittingRef.current) return;

    onClose?.();
  };

  const uploadThumbnail = async (file) => {
    if (!file) return null;

    setThumbnailUploading(true);
    setThumbnailProgress(0);

    const compressedFile = await compressImageFile({
      file,
      maxWidth: 1280,
      maxHeight: 720,
      quality: 0.72,
      outputType: "image/jpeg",
    });

    const response = await service.createStreamThumbnailUploadUrl(
      compressedFile.type,
    );

    const { uploadUrl, thumbnailUrl } = response?.data || {};

    if (!uploadUrl || !thumbnailUrl) {
      throw new Error("Unable to prepare the thumbnail upload.");
    }

    await uploadThumbnailToS3({
      uploadUrl,
      file: compressedFile,
      onProgress: (value) => {
        if (mountedRef.current) {
          setThumbnailProgress(value);
        }
      },
    });

    if (mountedRef.current) {
      setThumbnailProgress(100);
    }

    return thumbnailUrl;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isBusy || submittingRef.current) return;

    if (!username) {
      setFormError("Creator information is missing.");
      return;
    }

    if (!title) {
      setFormError("Stream title is required.");
      return;
    }

    if (!validStreamType || !validAccessType) {
      setFormError("Please select valid stream settings.");
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setFormError("");

    try {
      const thumbnailUrl = await uploadThumbnail(formData.thumbnailFile);

      const createdStream = await createStream({
        title,
        description,
        streamType: formData.streamType,
        accessType: formData.accessType,
        scheduledStart: formData.scheduledStart || null,
        recordingEnabled: formData.recordingEnabled ? 1 : 0,
        thumbnailUrl,
      });

      if (createdStream === false || createdStream?.success === false) {
        setFormError(createdStream?.message || "Unable to create stream.");
        return;
      }

      await onSuccess?.(createdStream);

      resetForm();
      onClose?.();
    } catch (submitError) {
      console.error("Unable to create stream:", submitError);

      if (mountedRef.current) {
        setFormError(getRequestError(submitError, "Unable to create stream."));
      }
    } finally {
      submittingRef.current = false;

      if (mountedRef.current) {
        setThumbnailUploading(false);
        setSubmitting(false);
      }
    }
  };

  return createPortal(
    <form
      className="creator-create-stream-form"
      onSubmit={handleSubmit}
      aria-label="Create Stream"
      aria-busy={isBusy}
    >
      <header className="creator-create-stream-form__header">
        <div className="creator-create-stream-form__header-info">
          <h2>Create Stream</h2>
          <p>Set up and manage your live stream on Katch.</p>
        </div>

        <button
          type="button"
          className="creator-create-stream-form__close"
          onClick={handleClose}
          disabled={isBusy}
          aria-label="Close create stream form"
        >
          <X size={20} />
        </button>
      </header>

      <div className="creator-create-stream-form__workspace">
        <main className="creator-create-stream-form__main">
          <div className="creator-create-stream-form__section-header">
            <h3>Stream Information</h3>
            <p>Provide details about your live stream.</p>
          </div>

          <div className="creator-create-stream-form__fields">
            <TextField
              label="Stream Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Bucks vs Harlequins"
              maxLength={120}
              required
              disabled={isBusy}
            />

            <TextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Add information about your stream"
              maxLength={1000}
              maxHeight={180}
              disabled={isBusy}
            />
          </div>

          <section className="creator-create-stream-form__thumbnail">
            <div className="creator-create-stream-form__section-header">
              <h3>Stream Thumbnail</h3>
              <p>Optional · Recommended 1280 × 720</p>
            </div>

            {formData.thumbnailPreview ? (
              <div className="creator-create-stream-form__preview">
                <img
                  src={formData.thumbnailPreview}
                  alt="Stream thumbnail preview"
                />

                <button
                  type="button"
                  className="creator-create-stream-form__remove-thumbnail"
                  onClick={handleRemoveThumbnail}
                  disabled={isBusy}
                  aria-label="Remove thumbnail"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <label className="creator-create-stream-form__thumbnail-picker">
                <ImagePlus size={28} strokeWidth={1.5} />
                <strong>Add Thumbnail</strong>
                <span>Choose a JPG, PNG or WebP image</span>

                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleThumbnailChange}
                  disabled={isBusy}
                  hidden
                />
              </label>
            )}

            {thumbnailUploading && (
              <div
                className="creator-create-stream-form__progress"
                role="status"
                aria-live="polite"
              >
                <div className="creator-create-stream-form__progress-header">
                  <span>Uploading thumbnail</span>
                  <strong>{progress}%</strong>
                </div>

                <div
                  className="creator-create-stream-form__progress-track"
                  role="progressbar"
                  aria-label="Thumbnail upload progress"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                >
                  <div
                    className="creator-create-stream-form__progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </section>
        </main>

        <aside className="creator-create-stream-form__sidebar">
          <div className="creator-create-stream-form__section-header">
            <h3>Stream Settings</h3>
            <p>Manage your broadcast and access options.</p>
          </div>

          <div className="creator-create-stream-form__fields">
            <Select
              label="Stream Type"
              name="streamType"
              value={formData.streamType}
              onChange={handleInputChange}
              items={STREAM_TYPE_OPTIONS}
              required
              disabled={isBusy}
            />

            <Select
              label="Access Type"
              name="accessType"
              value={formData.accessType}
              onChange={handleInputChange}
              items={ACCESS_TYPE_OPTIONS}
              required
              disabled={isBusy}
            />

            <p className="creator-create-stream-form__help">
              {getAccessNote(formData.accessType)}
            </p>

            <TextField
              label="Scheduled Start"
              name="scheduledStart"
              type="datetime-local"
              value={formData.scheduledStart}
              onChange={handleInputChange}
              disabled={isBusy}
            />

            <label className="creator-create-stream-form__checkbox">
              <input
                type="checkbox"
                name="recordingEnabled"
                checked={formData.recordingEnabled}
                onChange={handleInputChange}
                disabled={isBusy}
              />

              <span>Enable recording after the live stream</span>
            </label>
          </div>

          <div className="creator-create-stream-form__summary">
            <h4>Stream Summary</h4>

            <div>
              <span>Type</span>
              <strong>{formData.streamType}</strong>
            </div>

            <div>
              <span>Access</span>
              <strong>{formData.accessType}</strong>
            </div>

            <div>
              <span>Recording</span>
              <strong>
                {formData.recordingEnabled ? "Enabled" : "Disabled"}
              </strong>
            </div>

            <div>
              <span>Thumbnail</span>
              <strong>{formData.thumbnailFile ? "Added" : "Not added"}</strong>
            </div>
          </div>
        </aside>
      </div>

      <footer className="creator-create-stream-form__footer">
        <div className="creator-create-stream-form__footer-status">
          {errorMessage ? (
            <span className="creator-create-stream-form__error" role="alert">
              {errorMessage}
            </span>
          ) : thumbnailUploading ? (
            <span>Uploading thumbnail · {progress}%</span>
          ) : submitting || loading ? (
            <span>Creating stream...</span>
          ) : null}
        </div>

        <div className="creator-create-stream-form__actions">
          <Button
            type="button"
            value="Cancel"
            variant="primary-outlined"
            onClick={handleClose}
            disabled={isBusy}
          />

          <Button
            type="submit"
            value={submitLabel}
            variant="primary"
            disabled={!canSubmit}
          />
        </div>
      </footer>
    </form>,
    document.body,
  );
}
